/**
 * The **Trend** section — recent history for one status entity
 * (REQ-STATUS-064, -070, -072, -073; Flow L §4 item 7).
 *
 * Replaces the placeholder that shipped with the System View elaboration, which
 * passed a hardcoded empty array and so rendered *"the timeline view is a future
 * feature"* on every component, forever.
 *
 * # Three honesty rules this section is built around
 *
 *   1. **Gaps are gaps.** A `null` bucket breaks the line rather than joining
 *      across it, and a metric absent from the whole window renders **no row at
 *      all** — the server omits it (REQ-STATUS-073). A flat line at zero reads as
 *      *steady*, which is a different claim from *absent*.
 *   2. **An omitted series says why.** Beyond raw retention the state band and
 *      the domain metrics do not exist, and the section states the reason rather
 *      than quietly showing fewer rows (REQ-STATUS-067).
 *   3. **Nothing is fetched while collapsed** (REQ-STATUS-072).
 */
import { SectionShell } from "@/entity-details/section-shell";
import { usePaneHistoryWindow } from "@/entity-details/use-pane-history-window";
import { useEntityDetailsSectionsContext } from "@/entity-details/entity-details-sections-context";
import { StateBand } from "@/system-status/status-detail-primitives";
import {
  HISTORY_WINDOWS,
  HISTORY_WINDOW_LABELS,
  useComponentHistory,
  type HistoryWindow,
} from "@/system-status/use-component-history";
import {
  useMetricCatalog,
  resolveDomainMetric,
  resolveResourceMetric,
  type ResolvedMetric,
} from "@/system-status/use-metric-catalog";
import { TrendSeriesRow } from "@/system-status/trend-series-row";
import { useTrendExpansion } from "@/system-status/use-trend-expansion";

/** Machine-readable `unavailable` reasons → operator copy. */
const UNAVAILABLE_COPY: Record<string, string> = {
  beyond_raw_retention: "State history covers the last 14 days.",
};

/**
 * A trend row's display label.
 *
 * Domain metrics resolve through the **same catalog entry** as the value row
 * above them, so a metric cannot be called one thing in *Domain metrics* and
 * another in *Trend*. Resource series keep their fixed labels.
 */
function seriesLabel(
  key: string,
  componentKind: string,
  catalog: ReturnType<typeof useMetricCatalog>["data"],
): string {
  if (key === "cpu_pct") return "CPU";
  if (key === "mem_pct") return "Memory";
  const domainKey = key.startsWith("domain.") ? key.slice("domain.".length) : key;
  const resolved = catalog ? resolveDomainMetric(catalog, componentKind, domainKey) : null;
  return resolved?.label ?? domainKey;
}

/**
 * What the server did to produce these points, in operator words.
 *
 * `"max"` (REQ-STATUS-064, architect 2026-08-02) marks a per-bucket maximum: at
 * any window answered by a continuous aggregate the resource series is
 * `cpu_pct_max`, not the sampled value. A max-line and a mean-line diverge most
 * exactly when load is spiky — which is when someone is looking at it — so the
 * line says which it is.
 *
 * Read from the **response**, never re-derived from the selected window: which
 * windows are aggregate-sourced is the server's routing table's business, and a
 * second copy of it here is a copy that drifts. It is also per-series rather than
 * per-response, because domain metrics read raw at every window and would be
 * mislabelled by a window-level flag.
 */
function aggregateSuffix(aggregate: string): string {
  if (aggregate === "rate") return " per second";
  if (aggregate === "max") return " (max)";
  return "";
}

/**
 * The catalog sentence for a trend row, or `undefined` where there is none.
 *
 * Resource series resolve through the catalog's resource entries so CPU and
 * Memory carry the same wording as the rows above them; domain series resolve by
 * the `(component_kind, metric_key)` pair, exactly as Domain metrics does
 * (REQ-STATUS-053). An unmapped key yields nothing, and the label renders with
 * no trigger rather than a control with nothing to say (REQ-STATUS-054).
 */
function seriesMetric(
  key: string,
  componentKind: string,
  catalog: ReturnType<typeof useMetricCatalog>["data"],
): ResolvedMetric | null {
  if (catalog === undefined) return null;
  if (!key.startsWith("domain.")) return resolveResourceMetric(catalog, key);
  return resolveDomainMetric(catalog, componentKind, key.slice("domain.".length));
}

function seriesDescription(
  key: string,
  componentKind: string,
  catalog: ReturnType<typeof useMetricCatalog>["data"],
): string | undefined {
  if (catalog === undefined) return undefined;
  // Resource series live under the catalog's `resource` tier, keyed by field
  // name and NOT by component kind — the same lookup `CatalogResourceRow` does,
  // so "CPU" is explained identically in both sections.
  if (!key.startsWith("domain.")) {
    return resolveResourceMetric(catalog, key)?.tooltip;
  }
  return resolveDomainMetric(catalog, componentKind, key.slice("domain.".length))?.tooltip;
}

/** The full display name of a trend row — label plus what was done to it. */
function seriesDisplayName(
  series: { key: string; aggregate: string },
  componentKind: string,
  catalog: ReturnType<typeof useMetricCatalog>["data"],
): string {
  return `${seriesLabel(series.key, componentKind, catalog)}${aggregateSuffix(series.aggregate)}`;
}

/**
 * Order the plotted series to match the rest of the pane (architect, 2026-08-03).
 *
 * The sections above read **Health rollup → Resource utilization → Domain
 * metrics**, and Domain metrics renders `Object.entries(component.domain)` — the
 * order the metrics arrive in on the wire. The trend series arrived in a
 * different order entirely: the history query returns domain metrics
 * `ORDER BY kv.key`, i.e. alphabetically. So the same metrics appeared in two
 * orders in one panel, and an operator scanning down for a metric they had just
 * read the value of found it somewhere else.
 *
 * Resource series lead, in the same order Resource utilization lists them; then
 * domain series in the caller's wire order; then anything the caller did not
 * name, keeping its relative order so a new metric appears rather than vanishes.
 */
function orderSeries<T extends { key: string }>(
  series: readonly T[],
  domainOrder: readonly string[],
): T[] {
  const rank = (key: string): number => {
    const resource = RESOURCE_SERIES_ORDER.indexOf(key);
    if (resource !== -1) return resource;
    const domainKey = key.startsWith("domain.") ? key.slice("domain.".length) : key;
    const named = domainOrder.indexOf(domainKey);
    // Unnamed keys sort after everything named, not to the front.
    return named === -1
      ? RESOURCE_SERIES_ORDER.length + domainOrder.length
      : RESOURCE_SERIES_ORDER.length + named;
  };
  // Stable: equal ranks (all the unnamed ones) keep their arrival order.
  return [...series].sort((a, b) => rank(a.key) - rank(b.key));
}

/** Resource series in the order **Resource utilization** lists them. */
const RESOURCE_SERIES_ORDER: readonly string[] = ["cpu_pct", "mem_pct"];

export function StatusTrendSection({
  componentId,
  componentKind,
  entityLabel,
  domainOrder = [],
}: {
  componentId: string | null;
  componentKind: string;
  /** What the trends are *of* — used in the accessible names (REQ-STATUS-070). */
  entityLabel: string;
  /**
   * The component's domain metric keys **in the order the other sections render
   * them** (`Object.keys(component.domain)`). Passed in rather than re-derived
   * here because the pane's own wire order is the thing being matched — deriving
   * a second ordering, however reasonable, is how the two drift apart again.
   */
  domainOrder?: readonly string[];
}): React.ReactElement {
  const [window, setWindow] = usePaneHistoryWindow();
  // Session-local, one set, shared with every other trend surface.
  const expansion = useTrendExpansion();
  const sections = useEntityDetailsSectionsContext();
  // REQ-STATUS-072 — a collapsed section issues no request. A context-less
  // caller (an isolated test) is treated as open, matching SectionShell.
  const expanded = sections === null ? true : sections.sections.isExpanded("status-trend");
  const history = useComponentHistory({ componentId, window, enabled: expanded });
  const catalog = useMetricCatalog();

  const data = history.data;
  const unavailable = Object.values(data?.unavailable ?? {});

  return (
    <SectionShell
      id="status-trend"
      title="Trend"
      actions={
        // Only once collapsing is more than one click. At zero or one
        // expansion the series' own toggle already does the job, and a control
        // that does nothing in the overwhelmingly common case is clutter on a
        // dense pane. The COUNT is in the accessible name so activating it is
        // not a guess about how much is about to disappear.
        expansion.count > 1 ? (
          <button
            type="button"
            data-testid="trend-collapse-all"
            aria-label={`Collapse all ${expansion.count} expanded charts`}
            onClick={expansion.collapseAll}
            className="rounded px-1.5 py-0.5 text-meta text-text-secondary hover:bg-bg-elevated hover:text-text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
          >
            Collapse all
          </button>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-3" data-testid="status-trend-body">
        <label className="flex items-center gap-2 text-meta text-text-secondary">
          <span>Window</span>
          <select
            data-testid="trend-window"
            className="rounded border border-border bg-bg-elevated px-1.5 py-0.5 text-meta text-text-primary"
            value={window}
            onChange={(event) => setWindow(event.target.value as HistoryWindow)}
          >
            {HISTORY_WINDOWS.map((value) => (
              <option key={value} value={value}>
                {HISTORY_WINDOW_LABELS[value]}
              </option>
            ))}
          </select>
        </label>

        {history.isPending ? (
          <p className="text-meta italic text-text-muted">Loading history…</p>
        ) : data === undefined ? (
          <p className="text-meta italic text-text-muted" data-testid="trend-unavailable">
            History is not available right now.
          </p>
        ) : (
          <>
            {data.state && data.state.points.length > 1 && (
              <StateBand
                points={data.state.points}
                label="Status"
                accessibleName={`${entityLabel} status, ${HISTORY_WINDOW_LABELS[window]}`}
              />
            )}
            {orderSeries(data.series, domainOrder).map((series) => {
              const name = seriesDisplayName(series, componentKind, catalog.data);
              const accessibleName = `${name}, ${HISTORY_WINDOW_LABELS[window]}`;
              // The SAME catalog sentence the value row carries, resolved
              // through the same `(component_kind, metric_key)` pair — so a
              // metric cannot be explained in Domain metrics and bare in Trend.
              const description = seriesDescription(series.key, componentKind, catalog.data);
              const metric = seriesMetric(series.key, componentKind, catalog.data);
              return (
                <TrendSeriesRow
                  key={series.key}
                  points={series.points}
                  label={name}
                  accessibleName={accessibleName}
                  description={description}
                  unit={metric?.unit}
                  threshold={metric?.threshold}
                  window={window}
                  expanded={expansion.isExpanded(series.key)}
                  onToggle={() => expansion.toggle(series.key)}
                  testId={`trend-${series.key}`}
                />
              );
            })}
            {/* An absence with a cause is stated. Rendering fewer rows without
                saying why is what makes a long window look like a broken pane. */}
            {unavailable.map((reason) => (
              <p key={reason} className="text-meta italic text-text-muted" data-testid="trend-note">
                {UNAVAILABLE_COPY[reason] ?? "Some history is not available at this window."}
              </p>
            ))}
            {data.series.length === 0 && data.state === null && unavailable.length === 0 && (
              <p className="text-meta italic text-text-muted" data-testid="trend-empty">
                Not enough history yet.
              </p>
            )}
          </>
        )}
      </div>
    </SectionShell>
  );
}
